/**
 * Which of the ticket chat's verbs each sheet button is (ADR-0051).
 *
 * The chat does at least what the sheet does, through the same write path, so
 * every button the footer can draw is one of three things: a chat verb
 * (`availableItemVerbs` offers it over the same facts), a Held button (the
 * page's own, external or not undoable, `HELD_PAGE_BUTTONS`), or a move of
 * the reader's (open, next, watch) that acts on nothing. The parity test pins
 * the map; the Ask dock's first chip reads it.
 */
import type { ItemVerb, WorkItemGate } from "@yagni/shared";
import { readJobGatePayload, resumesApprovedPlan } from "@yagni/shared";
import type { SheetVerbId } from "./sheetFooter.js";

/** The sheet buttons the chat never presses: the page's own (`HELD_PAGE_BUTTONS`). */
export const HELD_SHEET_VERBS: readonly SheetVerbId[] = [
  "merge",
  "confirm_review",
  "confirm_held_review",
  "mark_done",
  "request_result_changes",
  "commission",
  "push_milestone",
  "take_over",
];

/** The sheet buttons that move the reader and act on nothing. */
export const NAVIGATION_SHEET_VERBS: readonly SheetVerbId[] = [
  "watch_live",
  "open_pr",
  "next_item",
  "open_successor",
  "retry_gate",
  "ask_yagni",
  "ask_about_plan",
  "ask_to_join",
  "open_link",
];

/** The chat's verbs no sheet button carries. */
export const CHAT_ONLY_VERBS: readonly ItemVerb[] = ["edit_fields"];

/** The Job acts the chat answers on a gate; every other one is the page's (Held). */
const CHAT_JOB_ACTS: ReadonlySet<string> = new Set([
  "accept_proposal",
  "proceed_to_build",
  "push_fix",
]);

/** The chat verb behind one sheet button on this gate, or why there is none. */
export function chatVerbOfSheetVerb(
  id: SheetVerbId,
  gate: WorkItemGate | null,
): ItemVerb | "held" | "navigation" {
  if (HELD_SHEET_VERBS.includes(id)) return "held";
  if (NAVIGATION_SHEET_VERBS.includes(id)) return "navigation";
  switch (id) {
    case "start_plan":
    // A queued tracker ticket's accept is Scope now on the server
    // (`trackerStartPlan`): the reading starts, never the plan.
    case "scope_now":
      return "accept_proposal";
    case "keep_proposal":
      return "decline_proposal";
    case "approve_plan":
      return "approve_plan";
    case "send_back":
      return "send_back_plan";
    case "send_answer":
    case "run_again_gate":
      return "answer";
    case "continue_anyway":
      return "continue_anyway";
    case "patch_plan":
      return "patch_plan";
    case "reoffer_plan":
      // A stop that keeps the approved plan runs it again the way Run again does.
      return gate && resumesApprovedPlan(gate) ? "answer" : "reoffer_plan";
    case "send_back_to_plan":
      return "stop_to_planning";
    case "dismiss_ask":
      return "dismiss_ask";
    case "keep_for_human":
      return "close_item";
    case "stop_run":
      return "stop_run";
    case "add_note":
      return "leave_next_step_note";
    case "run_again":
      return "run_again";
    case "hand_back":
      return "hand_back";
    case "start_over":
      return "start_over";
    case "start_planning":
      return "start_planning";
    case "request_changes":
      return "request_changes";
    case "one_more_round":
      return "approve_act";
    case "stop_round":
      return "decline_act";
    case "act":
    case "decline_act": {
      const act = gate ? readJobGatePayload(gate.payload)?.act : null;
      if (!act || !CHAT_JOB_ACTS.has(act)) return "held";
      return id === "act" ? "approve_act" : "decline_act";
    }
    default:
      return "held";
  }
}

/**
 * The Ask dock's first chip: the sheet's primary button, when the chat can do
 * what it does. Never on a review case (it has no footer), and never a Held
 * button or a move of the reader's.
 */
export function primaryChipOf(
  primary: { id: SheetVerbId; label: string } | null,
  gate: WorkItemGate | null,
  item: { kind?: string | null },
): string | null {
  if (!primary || item.kind === "review") return null;
  const verb = chatVerbOfSheetVerb(primary.id, gate);
  return verb === "held" || verb === "navigation" ? null : primary.label;
}
